import { keyframes } from '@emotion/react'
import {
  createMultiStyleConfigHelpers,
  MultiStyleType,
  StyleThemingProps,
} from '~/utils/chakraUtils'
import { mergeMultiStyleConfig } from '~/utils/styleHelpers'

import { asChakraVariable } from '@mangovoice/design-system'
import { textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'

const pendingMessageBubbleParts = [
  'container',
  'bubble',
  'statusText',
  'retryText',
  'messageTextWrapper',
  'messageText',
  'messageImage',
] as const

export enum PendingMessageBubbleVariants {
  Sending = 'sending',
  SendingEnlarged = 'sendingEnlarged',
  Failed = 'failed',
  FailedEnlarged = 'failedEnlarged',
}

const helpers = createMultiStyleConfigHelpers(pendingMessageBubbleParts)

const GradientBackgroundKeyframes = keyframes`
0% {
  background-position: -1000px 0;
}
100% {
  background-position: 1000px 0;
}
/* Specify background size in keyframes animation */
0%, 100% {
  background-size: 1000px;
}
`

export const GradientBackgroundSendingAnimation = `${GradientBackgroundKeyframes} 3s linear infinite forwards`

export const PendingMessageBubbleStyle = helpers.defineMultiStyleConfig({
  baseStyle: {
    container: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      width: '100%',
    },
    bubble: {
      wordBreak: 'break-word',
      maxWidth: '55%',
      borderWidth: 0,
      padding: '8px',
      paddingLeft: '12px',
      paddingRight: '12px',
      borderTopRightRadius: '16px',
      borderBottomLeftRadius: '16px',
      borderTopLeftRadius: '16px',
      borderBottomRightRadius: '0px',
      boxSizing: 'border-box',
      backgroundColor: 'brand.subtle',
    },
    messageImage: {
      maxHeight: '150px',
      width: 'auto',
      maxWidth: '100%',
      borderRadius: '5px',
    },
    statusText: {
      color: 'fg.subtle',
      ...textStyles[textStyleKeys.Body4],
      paddingRight: '8px',
      _focusVisible: {
        outline: '2px solid',
        outlineColor: 'brand.solid',
        outlineOffset: '2px',
        borderRadius: '2px',
      },
    },
    retryText: {
      color: 'red.fg',
      ...textStyles[textStyleKeys.Body4_Bold],
      cursor: 'pointer',
      display: 'inline',
      textDecoration: 'underline',
    },
    messageTextWrapper: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      gap: '16px',
    },
    messageText: {
      color: 'fg.default',
      ...textStyles[textStyleKeys.Body3],
    },
  },
  variants: {
    [PendingMessageBubbleVariants.Sending]: {
      bubble: {
        backgroundSize: '1000px',
        background: `linear-gradient(to right, ${asChakraVariable({ default: 'brand.muted' })} 0%, ${asChakraVariable({ default: 'brand.emphasized' })} 20%, ${asChakraVariable({ default: 'brand.muted' })} 90%)`,
      },
    },
    [PendingMessageBubbleVariants.SendingEnlarged]: {
      bubble: {
        backgroundSize: '1000px',
        background: `linear-gradient(to right, ${asChakraVariable({ default: 'brand.muted' })} 0%, ${asChakraVariable({ default: 'brand.emphasized' })} 20%, ${asChakraVariable({ default: 'brand.muted' })} 90%)`,
        paddingLeft: '8px',
        paddingRight: '8px',
      },
      messageText: {
        display: 'block',
        lineHeight: '36px',
        fontSize: '42px',
      },
    },
    [PendingMessageBubbleVariants.Failed]: {
      bubble: {
        backgroundColor: 'red.subtle',
      },
      statusText: {
        color: 'red.fg',
        cursor: 'pointer',
      },
    },
    [PendingMessageBubbleVariants.FailedEnlarged]: {
      bubble: {
        backgroundColor: 'red.subtle',
        paddingLeft: '8px',
        paddingRight: '8px',
      },
      statusText: {
        color: 'red.fg',
      },
    },
  },
  defaultProps: {
    variant: PendingMessageBubbleVariants.Sending,
  },
})

export const usePendingMessageBubbleStyles = (props?: StyleThemingProps) => {
  return mergeMultiStyleConfig(
    PendingMessageBubbleStyle,
    props,
  ) as MultiStyleType<typeof pendingMessageBubbleParts>
}
